import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { isAppPath } from "../lib/app-nav";

type Props = {
  children?: ReactNode;
  /** Internal SPA path - React Router (never raw href for /app/*). */
  to?: string;
  /** External / shopify:// / mailto. */
  href?: string;
  target?: string;
  variant?: string;
  tone?: string;
  type?: string;
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  accessibilityLabel?: string;
  slot?: string;
  commandFor?: string;
  command?: string;
  onClick?: () => void;
};

/**
 * Polaris `s-button` with SPA-safe navigation.
 * Page-slot buttons omit `variant` (Chrome/App Bridge click bug).
 * @see https://community.shopify.dev/t/page-slot-buttons-not-working/26839
 */
export function AppButton({
  children,
  to,
  href,
  target,
  variant,
  tone,
  type = "button",
  disabled,
  loading,
  icon,
  accessibilityLabel,
  slot,
  commandFor,
  command,
  onClick,
}: Props) {
  const navigate = useNavigate();
  const raw = to || href;
  const internal = raw && isAppPath(raw) ? raw : null;
  const external =
    !internal && raw && !isAppPath(raw) ? raw : !internal && href ? href : undefined;
  const inPageSlot =
    slot === "primary-action" || slot === "secondary-actions";
  const resolvedVariant = inPageSlot ? undefined : variant;
  /** Leave the app → new tab (Shopify admin, theme editor, mailto, etc.). */
  const externalTarget = external ? target || "_blank" : undefined;

  return (
    <s-button
      type={type}
      variant={resolvedVariant}
      tone={tone}
      disabled={disabled}
      loading={loading}
      icon={icon}
      accessibilityLabel={accessibilityLabel}
      slot={slot}
      commandFor={commandFor}
      command={command}
      href={external}
      target={externalTarget}
      {...(external
        ? ({ rel: "noopener noreferrer" } as Record<string, string>)
        : {})}
      onClick={() => {
        if (disabled || loading) return;
        onClick?.();
        if (internal) navigate(internal);
      }}
    >
      {children}
    </s-button>
  );
}
